<template>
  <v-dialog v-model="dialog" :overlay="false" scrollable max-width="600px">
    <v-card tile flat>
      <v-app-bar flat dark color="primary">
        <v-app-bar-nav-icon><v-icon>far fa-check</v-icon></v-app-bar-nav-icon>
        <v-toolbar-title>In Underwriting</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click.native="closeDialog" dark>
          <v-icon>close</v-icon>
        </v-btn>
      </v-app-bar>
      <v-card flat tile class="ma-0 mb-0">
        <v-card-text>
          <v-form lazy-validation>
            <v-container fluid grid-list-md>
              <v-layout v-if="!showAcceptConfirmation && !showRejectConfirmation">
                <v-btn color="primary" @click="showRejectConfirmation = true">Decline to Offer</v-btn>
                <v-spacer></v-spacer>
                <v-btn color="success" @click="showAcceptConfirmation = true">Offer Made</v-btn>
              </v-layout>
              <v-layout v-if="showAcceptConfirmation">
                <v-flex xs12>
                  <v-card-text>
                    <v-container grid-list-md class="pa-0">
                      <v-layout>
                        <v-flex>
                          <v-text-field
                            label="Add funding offered"
                            v-model="fundingOffered"
                            prefix="£"
                            required
                            :onkeyup="formatCurrency()"
                          >
                          </v-text-field>
                        </v-flex>
                      </v-layout>
                      <v-layout>
                        <v-flex>
                          <v-text-field label="Monthly interest rate" suffix="%" type="number" v-model="interestRate">
                          </v-text-field>
                        </v-flex>
                        <v-flex>
                          <v-menu
                            ref="dateFundingOfferedMenu"
                            :close-on-content-click="false"
                            v-model="offeredMenu"
                            transition="scale-transition"
                            offset-y
                            full-width
                            :nudge-right="40"
                            min-width="290px"
                            :return-value.sync="dateFundingOffered"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                label="Date"
                                v-model="dateFundingOffered"
                                prepend-icon="event"
                                readonly
                                v-on="on"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              @change="$refs.dateFundingOfferedMenu.save(dateFundingOffered)"
                              no-title
                              scrollable
                              v-model="dateFundingOffered"
                            >
                              <v-spacer></v-spacer>
                              <v-btn text color="primary" @click="offeredMenu = false">Cancel</v-btn>
                            </v-date-picker>
                          </v-menu>
                        </v-flex>
                      </v-layout>
                    </v-container>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red" text @click.native="showAcceptConfirmation = false">Cancel</v-btn>
                    <v-btn color="success" :disabled="fundingOfferedCheck" @click="save('FUNDER OFFER MADE')">
                      Update
                    </v-btn>
                  </v-card-actions>
                </v-flex>
              </v-layout>
              <v-layout v-if="showRejectConfirmation">
                <v-flex xs12>
                  <v-card-title>
                    <h3 class="font-weight-bold">Are you sure you want to decline your offer?</h3>
                  </v-card-title>
                  <v-card-text>
                    <v-textarea box label="Reason for declining" v-model="rejectionReason"></v-textarea>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red" text @click.native="showRejectConfirmation = false">Cancel</v-btn>
                    <v-btn color="success" @click="save('FUNDER DECLINED TO OFFER')">Decline Offer</v-btn>
                  </v-card-actions>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';

export default {
  name: 'enquiry-in-underwriting-dialog',
  components: {},
  props: ['show', 'enquiry'],
  data: () => ({
    rejectionReason: null,
    showRejectConfirmation: false,
    showAcceptConfirmation: false,
    fundingOffered: null,
    interestRate: 0.0,
    dateFundingOffered: null,
    offeredMenu: null
  }),
  computed: {
    ...mapGetters(['EnquiryStatuses']),
    dialog: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      }
    },
    fundingOfferedCheck() {
      return _.isEmpty(this.fundingOffered) || this.fundingOffered < 1 || _.isEmpty(this.dateFundingOffered);
    }
  },
  methods: {
    ...mapActions(['addNotification', 'postCustomerComment', 'storeFunderEnquiry']),
    closeDialog() {
      this.dialog = false;
    },
    async save(statusName) {
      const enquiryStatus = _.find(this.EnquiryStatuses, ['name', statusName]);
      const updates = { enquiry_status_id: enquiryStatus.id };

      if (statusName === 'FUNDER OFFER MADE') {
        _.merge(updates, {
          funding_offered: parseFloat(this.fundingOffered.replace(/[^\d.]/g, '')),
          monthly_interest_rate: this.interestRate,
          date_funding_offered: this.dateFundingOffered
        });
      }

      if (this.rejectionReason) {
        await this.postCustomerComment({
          comment: this.rejectionReason,
          privacy: 'public',
          commentParams: { id: this.enquiry.id, type: 'enquiry' }
        });
      }

      await this.storeFunderEnquiry({
        id: this.enquiry.id,
        updates
      });

      this.addNotification({ text: `enquiry updated` });
      this.dialog = false;
      this.rejectionReason = null;
    },
    formatCurrency() {
      let value = this.fundingOffered;
      const intValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.]/g, '')) : value;

      this.fundingOffered =
        intValue > 0
          ? intValue.toLocaleString({
              style: 'currency',
              currency: 'GBP',
              currencyDisplay: 'symbol',
              minimumSignificantDigits: 0,
              maximumSignificantDigits: 0
            })
          : value;
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  div.list__tile,
  .subheader {
    padding: 0 16px 0 0;
  }

  input,
  label {
    font-size: 14px;
  }
}

address {
  font-style: normal;
}
</style>
